import { useEffect, useState } from "react";
import { LocalStageStream, Stage, StageEvents, StreamType, SubscribeType } from "amazon-ivs-web-broadcast";
import { getStoredToken } from "../utils/auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

// Split layouts stored by the API (LiveStreams.layout), named after the dividing line:
// vertical_split = side by side, horizontal_split = stacked, pip = guests in small boxes over the host
export const SPLIT_LAYOUTS = ["vertical_split", "horizontal_split", "pip"];

// Without guests on stage the host fills the frame; "single" with guests falls back to side by side
export const stageLayout = (layout, guestCount) =>
  !guestCount ? "single" : SPLIT_LAYOUTS.includes(layout) ? layout : SPLIT_LAYOUTS[0];

/**
 * IVS Real-Time stage of a live (host and approved guests/co-hosts): publishes `localStream` and returns the
 * other participants as { id, userId, username, role, videoStream, audioStream, mediaStream }.
 * The stage is created by the API on the first token and deleted when the live ends.
 */
const useLiveStage = (streamId, localStream, enabled) => {
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !streamId || !localStream) return;
    let stage = null;
    let cancelled = false;
    // participant id -> { info, tracks: { video, audio } }
    const remote = new Map();

    const publish = () => {
      const streams = new Map();
      remote.forEach(({ info, tracks }, id) => {
        const tracksList = [tracks.video, tracks.audio].filter(Boolean);
        if (!tracksList.length) return;
        streams.set(id, {
          id,
          userId: info.userId,
          username: info.attributes?.username || "",
          role: info.attributes?.role || "guest",
          videoStream: tracks.video ? new MediaStream([tracks.video]) : null,
          audioStream: tracks.audio ? new MediaStream([tracks.audio]) : null,
          mediaStream: new MediaStream(tracksList),
        });
      });
      setParticipants([...streams.values()]);
    };

    const updateTracks = (info, stageStreams, added) => {
      if (info.isLocal) return;
      const entry = remote.get(info.id) || { info, tracks: {} };
      stageStreams.forEach((s) => {
        const kind = s.streamType === StreamType.VIDEO ? "video" : "audio";
        if (added) entry.tracks[kind] = s.mediaStreamTrack;
        else if (entry.tracks[kind] === s.mediaStreamTrack) delete entry.tracks[kind];
      });
      remote.set(info.id, entry);
      publish();
    };

    const join = async () => {
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/live/${streamId}/stage/token`, {
          method: "POST",
          headers: { "Content-Type": "application/json", token: getStoredToken() },
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || "Unable to join the stage");
        if (cancelled) return;

        const localStreams = [
          ...localStream.getVideoTracks().map((track) => new LocalStageStream(track)),
          ...localStream.getAudioTracks().map((track) => new LocalStageStream(track)),
        ];
        stage = new Stage(data.result.token, {
          stageStreamsToPublish: () => localStreams,
          shouldPublishParticipant: () => true,
          shouldSubscribeToParticipant: () => SubscribeType.AUDIO_VIDEO,
        });
        stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (info, streams) => updateTracks(info, streams, true));
        stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED, (info, streams) => updateTracks(info, streams, false));
        stage.on(StageEvents.STAGE_PARTICIPANT_LEFT, (info) => {
          remote.delete(info.id);
          publish();
        });
        stage.on(StageEvents.STAGE_LEFT, () => {
          remote.clear();
          publish();
        });
        await stage.join();
        if (cancelled) stage.leave();
      } catch (err) {
        console.error("Stage join error:", err);
        if (!cancelled) setError(err.message || "Unable to join the stage");
      }
    };

    join();
    return () => {
      cancelled = true;
      stage?.leave();
      setParticipants([]);
    };
  }, [streamId, localStream, enabled]);

  return { participants, error };
};

export default useLiveStage;
